import {noop, sum} from 'lodash';
import {Subject, timer} from 'rxjs';
import {catchError, filter, map, withLatestFrom} from 'rxjs/operators';

import {TaskSourcesEnum} from '../../libs/shared/helpers/src/instrumentation/eventLoopPerformanceHelpers';
import {ExtendedError} from '../../libs/shared/helpers/src/types/customErrors';
import {auditFirst} from '../rx/auditFirst';
import {bufferUnlessEmptyWhen} from '../rx/bufferUnlessEmptyWhen';
import {mapIdle} from '../rx/mapIdle';
import {mapRender} from '../rx/mapRender';
import {reportToBugsnag} from '../services/bugsnag/bugsnag';
import {logger} from '../services/logger/logger';

/*
 * Constants.
 */

/** Force tasks to be processed after this timeout. Corresponds to the idle callback timeout. */
const idleTimeout = 30 * 1000;

/** Consider tasks backlogged after this timeout. For instrumentation purposes. */
const backlogTimeout = 5 * 1000;

/** Report a non-zero backlog size at this interval.  */
const backlogLoggerInterval = 10 * 1000;

/** The maximum time to wait before trying to refetch the boot + P2. */
const maxReconnectDelay = 10 * 60 * 1000;

/** The base time to wait before trying to refetch the boot + P2. */
const baseReconnectDelay = 10 * 1000;

/*
 * Types.
 */

export type PushTask = PushIdleTask;
type PushIdleTask = (didTimeout: boolean) => PushRenderTask;
type PushRenderTask = () => void;

interface PushTaskQueueOptions {
  reconnectOnBacklogOptions?: PushReconnectOnBacklogOptions;
}

interface PushReconnectOnBacklogOptions {
  getLastReconnectTimestamp: () => number | null;
  refreshContent: (data: RefreshContentData) => void;
}

interface RefreshContentData {
  reconnectDelay: number;
  timeSinceReconnect: number;
  initialBacklogLength: number;
  currentBacklogLength: number;
}

const pushLogger = logger.getLogger('push');

export class PushTaskQueue {
  constructor(options: PushTaskQueueOptions = {}) {
    const {reconnectOnBacklogOptions} = options;
    this.taskBacklogLength$ = new Subject();
    this.taskBacklogLength$
      .pipe(
        // This is like a trailing throttle, except we buffer all the values.
        bufferUnlessEmptyWhen(() => timer(backlogLoggerInterval)),
      )
      .subscribe((samples) => {
        // Calculate the average backlog length during this period.
        const backlogLength = Math.round(sum(samples) / samples.length);

        // Don't report an empty backlog.
        if (backlogLength === 0) {
          return;
        }

        pushLogger.warn('Push event queue backlogged', {size: backlogLength});
      });

    if (reconnectOnBacklogOptions) {
      const {getLastReconnectTimestamp, refreshContent} = reconnectOnBacklogOptions;
      this.taskBacklogLength$
        .pipe(
          // Look at values where there are backlogged events.
          filter((backlogLength) => backlogLength > 0),
          map((backlogLength) => ({
            initialBacklogLength: backlogLength,
            reconnectDelay: computePushReconnectDelay(getLastReconnectTimestamp()),
            timeSinceReconnect: computeTimeSinceReconnect(getLastReconnectTimestamp()),
          })),
          // Wait until we're allowed to reconnect.
          auditFirst(({reconnectDelay}) => timer(reconnectDelay)),
          // Check the current backlog length.
          withLatestFrom(this.taskBacklogLength$),
          map(([initialBacklog, currentBacklogLength]) => ({...initialBacklog, currentBacklogLength})),
          // Continue only if there's still a backlog.
          filter(({currentBacklogLength}) => currentBacklogLength > 0),
        )
        .subscribe(
          (reconnectData) => refreshContent(reconnectData),
          (error) => {
            reportToBugsnag(new ExtendedError('Push backlog pipeline broken', error));
          },
          () => {
            reportToBugsnag(new Error('Push backlog pipeline unexpectedly completed'));
          },
        );
    }

    this.renderTaskBacklogLength$ = new Subject();
    this.renderTaskBacklogLength$
      .pipe(
        // This is like a trailing throttle, except we buffer all the values.
        bufferUnlessEmptyWhen(() => timer(backlogLoggerInterval)),
      )
      .subscribe((samples) => {
        // Calculate the average backlog length during this period.
        const backlogLength = Math.round(sum(samples) / samples.length);

        // Don't report an empty backlog.
        if (backlogLength === 0) {
          return;
        }

        pushLogger.warn('Push event render queue backlogged', {size: backlogLength});
      });

    this.tasks$ = new Subject();
    this.taskQueueCancels$ = new Subject();
    this.tasks$
      .pipe(
        // Perform the io-ts decoding and such in an idle callback.
        mapIdle(
          (idleTask, didTimeout) => {
            try {
              return idleTask(didTimeout);
            } catch (error) {
              reportToBugsnag(error);
              return noop;
            }
          },
          {
            timeout: idleTimeout,
            cancels$: this.taskQueueCancels$,
            backlogTimeout,
            backlogLengthObserver: this.taskBacklogLength$,
            metadata: {source: TaskSourcesEnum.PUSHER},
          },
        ),
        // Perform the rendering in an animation frame + react-redux batch.
        mapRender(
          (renderTask) => {
            try {
              return renderTask();
            } catch (error) {
              reportToBugsnag(error);
              return noop;
            }
          },
          {
            cancels$: this.taskQueueCancels$,
            backlogTimeout,
            backlogLengthObserver: this.renderTaskBacklogLength$,
            metadata: {source: TaskSourcesEnum.PUSHER},
          },
        ),
        // Just in case.
        catchError((error, caught$) => {
          reportToBugsnag(error);
          pushLogger.error('Push event queue error', {error: String(error)});

          // Restart the entire pipeline.
          return caught$;
        }),
      )
      .subscribe();
  }

  private tasks$: Subject<PushTask>;
  private taskQueueCancels$: Subject<void>;
  private taskBacklogLength$: Subject<number>;
  private renderTaskBacklogLength$: Subject<number>;

  /*
   * Public API.
   */

  enqueue(task: PushTask) {
    this.tasks$.next(task);
  }

  cancelAll() {
    this.taskQueueCancels$.next();
  }
}

/*
 * Helpers.
 */

function computeTimeSinceReconnect(lastReconnectTime: number | null) {
  if (!lastReconnectTime) {
    return Infinity;
  }

  return performance.now() - lastReconnectTime;
}

function computePushReconnectDelay(lastReconnectTime: number | null) {
  const k = maxReconnectDelay / computeTimeSinceReconnect(lastReconnectTime);

  const unjitteredDelay = Math.min(baseReconnectDelay * 2 ** k, maxReconnectDelay);
  const jitteredDelay = unjitteredDelay * Math.random();
  return jitteredDelay;
}
